// src/app/modules/feature-toggle/services/feature-toggle.service.ts
import { Injectable } from '@angular/core';
import { Observable, combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { SubscriptionService } from './subscription.service';
import { CompanySettingsService } from '../../settings/services/company-settings.service';
import { AuthService } from '../../auth/services/auth/auth.service';
import { Feature } from '../enums/features.enum';

@Injectable({
  providedIn: 'root',
})
export class FeatureToggleService {
  constructor(
    private subscriptionService: SubscriptionService,
    private companySettings: CompanySettingsService,
    private authService: AuthService
  ) {}

  /**
   * Zentrale Logik für Feature-Verfügbarkeit inkl. Settings und Berechtigungen
   */
  isFeatureAvailable(feature: Feature): Observable<boolean> {
    return combineLatest([
      this.subscriptionService.hasFeature$(feature),
      this.companySettings.CompanySettingsObservable,
      this.authService.currentUser$,
    ]).pipe(
      map(([hasFeature, settings, user]) => {
        // Basis-Check: Feature muss im Abonnement enthalten sein
        if (!hasFeature) return false;

        // Feature-spezifische Logik mit Settings und Berechtigungen
        switch (feature) {
          // Shift Planning Features
          case Feature.SHIFT_PLANNING_BASIC:
          case Feature.SHIFT_PLANNING_TEMPLATES:
          case Feature.SHIFT_PLANNING_AI:
            return (
              settings != null &&
              settings.ShiftSettings?.ShiftPlanerActive === true &&
              this.authService.isManegeOrTeamLeader
            );

          // Time Tracking Features
          case Feature.TIME_TRACKING_BASIC:
          case Feature.TIME_TRACKING_MOBILE:
          case Feature.TIME_TRACKING_TERMINAL:
          case Feature.TIME_TRACKING_NFC:
          case Feature.TIME_TRACKING_BIOMETRIC:
          case Feature.OVERTIME_MANAGEMENT:
            return (
              settings != null &&
              settings.TimeTrackingSettings?.TimeTrackingActive === true
            );

          // Reports - benötigen zusätzlich Time Tracking
          case Feature.REPORTS_BASIC:
          case Feature.REPORTS_ADVANCED:
          case Feature.CUSTOM_REPORTS:
            return (
              settings != null &&
              settings.TimeTrackingSettings?.TimeTrackingActive === true &&
              this.authService.isManegeOrTeamLeader
            );

          // Absence Management
          case Feature.ABSENCE_MANAGEMENT_BASIC:
          case Feature.ABSENCE_APPROVAL_WORKFLOW:
          case Feature.VACATION_MANAGEMENT:
          case Feature.SICK_LEAVE_MANAGEMENT:
            return this.authService.isManegeOrTeamLeader;

          // Admin-only Features
          case Feature.MULTI_LOCATION:
          case Feature.API_ACCESS:
          case Feature.WEBHOOKS:
          case Feature.SSO:
          case Feature.WHITE_LABEL:
          case Feature.CUSTOM_BRANDING:
            return this.authService.isAdmin;

          // Standard: Nur Feature-Check
          default:
            return true;
        }
      })
    );
  }

  /**
   * Synchrone Version für einfache Checks
   */
  isFeatureAvailableSync(feature: Feature): boolean {
    const hasFeature = this.subscriptionService.hasFeature(feature);
    if (!hasFeature) return false;

    const settings = this.companySettings.CompanySettings;

    switch (feature) {
      case Feature.SHIFT_PLANNING_BASIC:
      case Feature.SHIFT_PLANNING_TEMPLATES:
      case Feature.SHIFT_PLANNING_AI:
        return (
          settings != null &&
          settings.ShiftSettings?.ShiftPlanerActive === true &&
          this.authService.isManegeOrTeamLeader
        );

      case Feature.TIME_TRACKING_BASIC:
      case Feature.TIME_TRACKING_MOBILE:
      case Feature.TIME_TRACKING_TERMINAL:
        return (
          settings != null &&
          settings.TimeTrackingSettings?.TimeTrackingActive === true
        );

      case Feature.REPORTS_BASIC:
      case Feature.REPORTS_ADVANCED:
        return (
          settings != null &&
          settings.TimeTrackingSettings?.TimeTrackingActive === true &&
          this.authService.isManegeOrTeamLeader
        );

      default:
        return true;
    }
  }

  /**
   * Gibt alle verfügbaren Features mit ihrem Status zurück
   */
  getAllFeatureStatuses(): Observable<Map<Feature, boolean>> {
    return combineLatest([
      this.subscriptionService.features$,
      this.companySettings.CompanySettingsObservable,
      this.authService.currentUser$,
    ]).pipe(
      map(([features, settings, user]) => {
        const statusMap = new Map<Feature, boolean>();

        // Alle Feature-Enums durchgehen
        Object.values(Feature)
          .filter((value) => typeof value === 'number')
          .forEach((feature: Feature) => {
            const hasFeature = features.has(feature);
            if (!hasFeature) {
              statusMap.set(feature, false);
              return;
            }

            // Settings-basierte Verfügbarkeit prüfen
            statusMap.set(
              feature,
              this.checkFeatureWithSettings(feature, settings)
            );
          });

        return statusMap;
      })
    );
  }

  private checkFeatureWithSettings(feature: Feature, settings: any): boolean {
    if (!settings) return false;

    switch (feature) {
      case Feature.SHIFT_PLANNING_BASIC:
      case Feature.SHIFT_PLANNING_TEMPLATES:
        return settings.ShiftSettings?.ShiftPlanerActive === true;

      case Feature.TIME_TRACKING_BASIC:
      case Feature.TIME_TRACKING_MOBILE:
        return settings.TimeTrackingSettings?.TimeTrackingActive === true;

      default:
        return true;
    }
  }
}
